import { useState } from 'react';
import {
    Table,
    Whisper,
    Popover,
} from 'rsuite';
import { DaysOfWeekNames } from '../../../../../Enums';
import './AwardedSchedule.scss';
import { getShiftsForDay, getPatternRowNumber } from '../../../../../Employee/components/ShiftLineSelectionTable/ShiftLineSelectionTable';

const { Column, HeaderCell, Cell } = Table;

/**
 * @param {{
 *  scheduleTemplateItem: BidProfile.IScheduleTemplateItem,
 *  shiftsDictionary: BidProfile.ISchedulePatternsShiftTemplateDictionary,
 *  firstDayOfTheWeek: number,
 *  isWorkplan: boolean,
 *  tableHeight: number,
 *  dayCount: number;
 *  isPeriodTypeDay: boolean;
 *  daysPerPeriod: number;
 * }} props
 */
function AwardedSchedule(props) {
    const {
        scheduleTemplateItem,
        shiftsDictionary,
        firstDayOfTheWeek = 0,
        isWorkplan = false,
        tableHeight = 55,
        dayCount,
        isPeriodTypeDay,
        daysPerPeriod = 7,
    } = props;

    /**
     * @param {string} name
     * @param {import('rsuite/lib/@types/common').TypeAttributes.Placement} placement
     */
    const getStringWhisper = (name, placement = 'top') => {
        const tooltip = (
            <Popover visible>
                {name}
            </Popover>
        );

        return (
            <Whisper
                preventOverflow
                delay={500}
                delayOpen={500}
                delayClose={500}
                placement={placement}
                speaker={tooltip}
            >
                <span className="ds-text-cell-content">{name}</span>
            </Whisper>
        );
    };

    /**
     * @param {preparedRowData} orgJobPath
     */
    const getOrgJobWhisper = ({ fullOrgJobPath, orgJobPath }) => {
        const tooltip = (
            <Popover visible>
                {fullOrgJobPath}
            </Popover>
        );

        return (
            <Whisper
                placement="top"
                delay={500}
                delayOpen={500}
                delayClose={500}
                speaker={tooltip}
            >
                <span className="ds-text-cell-content-rtl">{orgJobPath}</span>
            </Whisper>
        );
    };

    /**
     * @typedef {{
     *  shifts: BidProfile.ISchedulePatternTemplateItem[][][],
     *  weeklyHeight: number[],
     * }} rowDataExtension
     * @typedef { BidProfile.IScheduleTemplateItem & rowDataExtension } preparedRowData
     * @param {BidProfile.IScheduleTemplateItem} item
     * @returns { preparedRowData }
     */
    const prepareTableItems = (item) => {
        const { items } = item.schedulePatternTemplate;
        const weeklyHeight = Object.keys(item.maxDayPerWeek).map((x) => item.maxDayPerWeek[x]);

        /** @type {BidProfile.ISchedulePatternTemplateItem[][][]} */
        const shifts = [];

        items.forEach((shift) => {
            const week = Math.floor((shift.day - 1) / 7);
            const dayOfWeek = (shift.day - 1) % 7;
            if (shifts[week] === undefined) {
                shifts[week] = [[], [], [], [], [], [], []];
            }

            shifts[week][dayOfWeek].push(shift);
        });

        return {
            ...item,
            weeklyHeight,
            shifts,
        };
    };

    const [preparedSchedulePatternItems] = useState(prepareTableItems(scheduleTemplateItem));
    // @ts-ignore
    const columns = [
        {
            id: 'Schedule',
            name: 'Schedule Name',
            width: 150,
            getRowData:
                (/** @type {preparedRowData} */ rowData) => getStringWhisper(rowData.schedulePatternTemplate.name, 'topStart'),
        },
        {
            id: 'Location',
            name: 'Location',
            width: isWorkplan ? 200 : 350,
            getRowData:
                /** @param {preparedRowData} rowData */
                (rowData) => getOrgJobWhisper(rowData),
        },
        {
            id: 'Row number',
            name: 'RowNumber',
            width: 100,
            getRowData:
                /** @param {preparedRowData} rowData */
                (rowData) => getPatternRowNumber(rowData),
        },
        ...Array.from(Array(daysPerPeriod).keys()).map((x) => ({
            id: `day_${x}`,
            name: isPeriodTypeDay ? `${x + 1}` : DaysOfWeekNames[(x + firstDayOfTheWeek) % daysPerPeriod],
            width: 150,
            getRowData:
                /** @param {preparedRowData} rowData */
                (rowData) => getShiftsForDay(x, shiftsDictionary, rowData, dayCount, daysPerPeriod, false),
        })),
    ];

    if (isWorkplan) {
        const workplanColumn = {
            id: 'Workplan',
            name: 'Workplan',
            width: 150,
            getRowData:
                /** @param {preparedRowData} rowData */
                (rowData) => getStringWhisper(rowData.workplanName, 'top'),
        };
        columns.splice(2, 0, workplanColumn);
    }

    return (
        <div>
            <Table
                key="bpm-detailed-selection"
                className="bpm-detailed-selection"
                data={[preparedSchedulePatternItems]}
                autoHeight
                rowHeight={tableHeight}
            >
                {columns.map((column) => (
                    <Column
                        key={column.id}
                        width={column.width}
                    >
                        <HeaderCell>
                            {column.name}
                        </HeaderCell>
                        <Cell>
                            {/** @param {preparedRowData} rowData */
                                (rowData) => column.getRowData(rowData)
                            }
                        </Cell>
                    </Column>
                ))}
            </Table>
        </div>
    );
}

export default AwardedSchedule;
